import React, { useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Divider,
  RadioGroup,
  Radio,
} from '@mui/material'
import Grid from '@mui/material/Grid2'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { ThemeApi } from '../../../api/themeApi'
import SectionSpacingFields from './shared/SectionSpacingFields'
import TextEditor from '../../Helpers/TextEditor'
import BannerSlideLinkPicker from './BannerSlideLinkPicker'
import useTranslation from '../../../hooks/useTranslation'
import toast from '../../../utils/toast'

const emptySlide = () => ({
  image: '',
  url: '',
  urlModuleName: '',
  urlResourceName: '',
  buttonText: '',
  headlineText: '',
  textColor: 'white',
  buttonPosition: 'left',
  size: 'large',
  overlayColor: 'black',
  name: '',
  visible: true,
  position: 0,
})

function InlineRadioGroup({ label, name, value, options, onChange, row = false }) {
  return (
    <FormControl component="fieldset" fullWidth>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <RadioGroup
        row={row}
        name={name}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio />}
            label={option.label}
          />
        ))}
      </RadioGroup>
    </FormControl>
  )
}

const stripHtml = (html) => {
  if (!html) return ''
  return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()
}

const truncate = (str, max = 40) => (str.length > max ? str.slice(0, max).trimEnd() + '…' : str)

export default function HeroBannerSettings({ settings, sectionId, themeId, onChange }) {
  const { t } = useTranslation()
  const [deleteSlideIndex, setDeleteSlideIndex] = useState(null)

  const radioOptions = {
    size: [
      { value: 'small', label: t('labels.themes.small') },
      { value: 'medium', label: t('labels.themes.medium') },
      { value: 'large', label: t('labels.themes.large') },
    ],
    buttonPosition: [
      { value: 'left', label: t('labels.themes.left') },
      { value: 'center', label: t('labels.themes.center') },
      { value: 'right', label: t('labels.themes.right') },
    ],
  }

  const slides = settings.slides || []

  const updateSlide = (index, patch) => {
    const newSlides = slides.map((slide, i) =>
      i === index ? { ...slide, ...patch } : slide
    )

    onChange({
      ...settings,
      slides: newSlides,
    })
  }

  const addSlide = () => {
    onChange({
      ...settings,
      slides: [...slides, { ...emptySlide(), position: slides.length }],
    })
  }

  const removeSlide = (index) => {
    onChange({
      ...settings,
      slides: slides.filter((_, i) => i !== index).map((slide, i) => ({
        ...slide,
        position: i,
      })),
    })
  }

  const handleImageUpload = async (index, file) => {
    try {
      const formData = new FormData()
      formData.append('image', file)
      const res = await ThemeApi.uploadSectionImage(themeId, sectionId, formData)

      updateSlide(index, {
        image: res.data.url,
        imagePreviewUrl: URL.createObjectURL(file),
      })
    } catch (err) {
      console.error('Image upload failed:', err)
      toast.error(t('labels.themes.imageUploadFailed'))
    }
  }

  const removeImage = (index) => {
    updateSlide(index, {
      image: '',
      imagePreviewUrl: '',
    })
  }

  const renderSlideImage = (slide, index) => {
    const imageSrc = slide.imagePreviewUrl || slide.image

    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          {t('labels.themes.image')}
        </Typography>

        {imageSrc ? (
          <Box
            sx={{
              width: '100%',
              height: 180,
              borderRadius: 1.5,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              mb: 2,
              backgroundColor: '#f8fafc',
            }}
          >
            <img
              src={imageSrc}
              alt={slide.name || `${t('labels.themes.slide')} ${index + 1}`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: 180,
              borderRadius: 1.5,
              border: '1px dashed',
              borderColor: 'divider',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
              backgroundColor: '#f8fafc',
            }}
          >
            {t('labels.themes.noImageSelected')}
          </Box>
        )}

        <Stack direction="row" spacing={1}>
          <Button
            component="label"
            variant="outlined"
            fullWidth
            startIcon={<CloudUploadIcon />}
          >
            {imageSrc ? t('labels.themes.replaceImage') : t('labels.themes.uploadImage')}
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => e.target.files?.[0] && handleImageUpload(index, e.target.files[0])}
            />
          </Button>

          <Button
            variant="outlined"
            color="error"
            disabled={!imageSrc}
            onClick={() => removeImage(index)}
          >
            {t('labels.themes.remove')}
          </Button>
        </Stack>
      </Paper>
    )
  }

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.autoplay !== false}
                onChange={e => onChange({ ...settings, autoplay: e.target.checked })}
              />
            }
            label={t('labels.themes.autoplay')}
          />

          <SectionSpacingFields
            settings={settings}
            onChange={onChange}
          />
        </Stack>
      </Paper>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2">
          {t('labels.themes.slides')} ({slides.length})
        </Typography>
        <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={addSlide}>
          {t('labels.themes.addSlide')}
        </Button>
      </Stack>

      {slides.map((slide, index) => (
        <Accordion key={index} defaultExpanded={slides.length === 1}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ width: '100%', pr: 1 }}
            >
              <Typography variant="body2">
                {slide.name || truncate(stripHtml(slide.headlineText)) || `${t('labels.themes.slide')} ${index + 1}`}
              </Typography>

              <Stack direction="row" alignItems="center" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                <Tooltip title={slide.visible !== false ? t('labels.themes.visible') : t('labels.themes.hidden')}>
                  <Switch
                    size="small"
                    checked={slide.visible !== false}
                    onChange={(e) => updateSlide(index, { visible: e.target.checked })}
                  />
                </Tooltip>

                <Tooltip title={t('labels.themes.delete')}>
                  <IconButton
                    size="small"
                    onClick={() => setDeleteSlideIndex(index)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </AccordionSummary>

          <AccordionDetails>
            <Stack spacing={3}>
              <TextField
                label={t('labels.themes.name')}
                size="small"
                fullWidth
                value={slide.name || ''}
                onChange={(e) => updateSlide(index, { name: e.target.value })}
                helperText={t('labels.themes.slideNameHelper')}
              />

              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {t('labels.themes.headlineText')}
                    </Typography>
                    <TextEditor
                      name={`headlineText-${index}`}
                      rows={8}
                      value={slide.headlineText || ''}
                      onChange={(content) => updateSlide(index, { headlineText: content })}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {t('labels.themes.headlineTextHelper')}
                    </Typography>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  {renderSlideImage(slide, index)}
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{t('labels.themes.textColor')}</InputLabel>
                    <Select
                      value={slide.textColor || 'white'}
                      label={t('labels.themes.textColor')}
                      onChange={(e) => updateSlide(index, { textColor: e.target.value })}
                    >
                      <MenuItem value="white">{t('labels.themes.white')}</MenuItem>
                      <MenuItem value="black">{t('labels.themes.black')}</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {t('labels.themes.textColorHelper')}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{t('labels.themes.overlayColor')}</InputLabel>
                    <Select
                      value={slide.overlayColor || 'black'}
                      label={t('labels.themes.overlayColor')}
                      onChange={(e) => updateSlide(index, { overlayColor: e.target.value })}
                    >
                      <MenuItem value="black">{t('labels.themes.black')}</MenuItem>
                      <MenuItem value="white">{t('labels.themes.white')}</MenuItem>
                      <MenuItem value="none">{t('labels.themes.none')}</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {t('labels.themes.overlayColorHelper')}
                  </Typography>
                </Grid>

                {slide.overlayColor !== 'none' && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label={t('labels.themes.overlayOpacity')}
                      type="number"
                      size="small"
                      fullWidth
                      inputProps={{ min: 0, max: 100, step: 5 }}
                      value={slide.overlayOpacity ?? 30}
                      onChange={(e) => updateSlide(index, { overlayOpacity: Math.min(100, Math.max(0, parseInt(e.target.value, 10) || 0)) })}
                      helperText={t('labels.themes.overlayOpacityHelp')}
                    />
                  </Grid>
                )}

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label={t('labels.themes.buttonText')}
                    size="small"
                    fullWidth
                    value={slide.buttonText || ''}
                    onChange={(e) => updateSlide(index, { buttonText: e.target.value })}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <BannerSlideLinkPicker
                    slide={slide}
                    onChange={(patch) => updateSlide(index, patch)}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <InlineRadioGroup
                    label={t('labels.themes.selectSplashImageSize')}
                    name={`size-${index}`}
                    value={slide.size || 'large'}
                    options={radioOptions.size}
                    onChange={(value) => updateSlide(index, { size: value })}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <InlineRadioGroup
                    label={t('labels.themes.selectButtonPosition')}
                    name={`button-position-${index}`}
                    value={slide.buttonPosition || 'left'}
                    options={radioOptions.buttonPosition}
                    onChange={(value) => updateSlide(index, { buttonPosition: value })}
                    row
                  />
                </Grid>

              </Grid>
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}

      <Dialog open={deleteSlideIndex !== null} onClose={() => setDeleteSlideIndex(null)}>
        <DialogTitle>{t('labels.themes.deleteSlide')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('labels.themes.deleteSlideDescription')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSlideIndex(null)}>{t('labels.themes.cancel')}</Button>
          <Button
            onClick={() => {
              removeSlide(deleteSlideIndex)
              setDeleteSlideIndex(null)
            }}
            variant="contained"
            color="error"
          >
            {t('labels.themes.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}